/*
 * Authors: mariushirjanu
 * Created: 06/09/2022
 */
import * as _ from 'underscore';
import { Component, OnInit, Input } from '@angular/core';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../shared/formly/ga-formly.service';
import { GaValidateService } from '../../../shared/formly/ga-validate.service';
import { BUTTON_SAVE, DefaultFormDialogComponent } from '../../../shared/message/dialogs/default-form-dialog.component';
import { TranslationService } from '../../../shared/translation/translation.service';
import { IPartConfiguration, IPartConfigurationOption, PartConfiguratorDataService } from './part-configurator-data.service';
import { ICatalogCartItem, PriceCartService } from './price-cart.service';
import { OrderCatalogService } from '../catalog/order-catalog.service';

export interface IPartConfiguratorModel {
  partConfiguration:IPartConfigurationOption,
  moreOptions:IPartConfigurationOption
}

@Component({
  selector: 'part-configurator-dialog',
  templateUrl: '../../../shared/message/dialogs/default-form-dialog.html'
})
export class PartConfiguratorDialogComponent extends DefaultFormDialogComponent implements OnInit {
  @Input() public hasPartOptions:boolean;
  @Input() public product:ICatalogCartItem;

  public dialogClass:string = 'part-configuration-form';
  public closeButton:boolean = false;
  public error:string;
  public model:IPartConfiguratorModel = <any>{};
  private partConfigurationField:IExtraFieldConfigurationObject;
  private moreOptionsField:IExtraFieldConfigurationObject;
  private hasSuffix:boolean;

  constructor(
    private formlyService:GaFormlyService,
    validateService:GaValidateService,
    translationService:TranslationService,
    private priceCartService:PriceCartService,
    private orderCatalogService:OrderCatalogService,
    private partConfiguratorDataService:PartConfiguratorDataService
  ) {
    super(validateService, translationService);
  }

  public ngOnInit():void {
    super.ngOnInit();
    this.buttons = [ {
      id: BUTTON_SAVE,
      label: 'GA.BUTTONS.OK',
      className: 'btn btn-success',
      onClick: this.add,
      busy: this.saving
    }, {
      label: 'GA.BUTTONS.CANCEL',
      className: 'btn btn-danger',
      onClick: () => this.onCancel.emit()
    } ];

    this.start();
  }

  private start():void {
    this.hasSuffix = !!(this.product.partSuffix && this.product.partSuffixDescription);
    this.fields = this.createFields();
    const countryId:number = this.orderCatalogService.countryId;

    this.loading = true;
    this.partConfiguratorDataService.getPartConfiguration(this.product.part, this.orderCatalogService.webshopId, countryId)
      .then((configuration:IPartConfiguration) => {
        if (configuration.partConfigurationExist) {
          this.hasPartOptions = !!configuration.partOptions.length;

          // Add at the end the option: 'More options'
          const partOptions:IPartConfigurationOption[] = _.clone(configuration.partOptions);
          partOptions.push({
            id: -1,
            description: this.translationService.instant('CATALOG.PART_CONFIGURATION_DIALOG.MORE_OPTIONS')
          });
          this.partConfigurationField.templateOptions.options = partOptions;

          if (configuration.partOptions) {
            this.preselectPartConfiguration(configuration);

            setTimeout(() => { // give some time to set the model
              this.formlyService.fillField(this.moreOptionsField, this.partConfiguratorDataService.getMoreOptions(countryId, configuration.configurationId, configuration.propertyDefinitionCode, configuration.category, this.product.part))
                .then((moreOptions:IPartConfigurationOption[]) => {
                  this.preselectPartConfiguration(configuration, moreOptions);
                });
              });
          }
        }
      })
      .catch(() => {
        // close the dialog if the getPartConfiguration REST fails
        this.onCancel.emit();
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private createFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aTitle(`${this.translationService.instant('CATALOG.PART_CONFIGURATION_DIALOG.KEYBOARD_LANGUAGE')}:`)
        .withWrapperClass('col-12 col-space-bottom')
        .withExtraOptions({
          templateOptions: {
            titleClass: '' // clear default titleClass
          }
        })
        .build(),

      this.partConfigurationField = this.formlyService.aRadio('partConfiguration', '')
        .withLabelClass('d-none')
        .withValueClass('col-md-12')
        .withFormat(this.priceCartService.formatPartDescription)
        .withHideExpression(() => !this.hasPartOptions)
        .onChange(() => {
          if (this.model.partConfiguration?.id !== -1) {
            this.model.moreOptions = undefined;
          }
          this.error = undefined;
        })
        .build(),

      this.moreOptionsField = this.formlyService.aSelect('moreOptions')
        .isRequired()
        .withLabelClass('d-none')
        .withValueClass('col-md-12')
        .withFormat(this.priceCartService.formatPartDescription)
        .withHideExpression(() => this.model.partConfiguration?.id !== -1 || !this.model.partConfiguration)
        .onChange(() => {
          this.error = undefined;
        })
        .build()
      ];
  }

  public add = ():void => {
    this.validateService.validateFields([
      this.partConfigurationField,
      this.moreOptionsField
    ], this.model, true)
      .then(() => {
        this.onSave.emit(this.model);
      })
      .catch(() => {
        this.error = 'GA.VALIDATION.SELECT_OPTION';
      });
  };

  // select the correct options for the form fields
  private preselectPartConfiguration(configuration:IPartConfiguration, moreOptions?:IPartConfigurationOption[]):void {
    let selectedPartConfiguration:IPartConfigurationOption = {
      id: -1,
      description: ''
    };
    let selectedMoreOptions:IPartConfigurationOption;

    if (this.hasSuffix) {
      // get selected part configuration
      const findSelectedPart:IPartConfigurationOption = _.find(configuration.partOptions, { productCategorySuffix: this.product.partSuffix });
      if (findSelectedPart) {
        selectedPartConfiguration = findSelectedPart;
      } else {
        if (moreOptions) {
          selectedMoreOptions = _.find(moreOptions, { productCategorySuffix: this.product.partSuffix });
        }
      }
    } else {
      if (configuration.partOptions.length) {
        selectedPartConfiguration = configuration.partOptions[ 0 ]; // auto select the first option if there is no option selected
      }
    }

    this.model = {
      partConfiguration: selectedPartConfiguration,
      moreOptions: selectedMoreOptions
    };
  }
}